import { useEffect } from "react";
import { IntlProvider } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@configs/Redux";
import { Router } from "@configs/Router";
import { flattenMessages, messages } from "@languages/index";
import {
  LANG,
  LOCAL_STORAGE_KEY,
  SESSION_STORAGE_KEY
} from "@resources/Constants";
import { GlobalStyles } from "@themes/GlobalStyles";
import { LayerPortal } from "@components/common/layer/LayerPortal";
import { ThemeProvider } from "@themes/ThemeProvider";
import useAccessibleOrganizations from "@hooks/resources/useAccessibleOrganizations";
import useLastAccessOrganization from "@hooks/resources/useLastAccessOrganization";
import useChangeOrganization from "@hooks/resources/useChangeOrganization";
import { setActiveOrganization } from "@redux/Organization";
import { setUserLanguage } from "@redux/System";
import { languageChannel } from "@utils/LanguageSync";
import { PageWrapperStyled } from "./PageWrapper.styled";

export function PageWrapper(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    lang
    // email
  } = useAppSelector((state) => state.system);
  const { data: organizations } = useAccessibleOrganizations();
  const { refetch: fetchLastAccessOrganization } =
    useLastAccessOrganization(false);
  const { mutateAsync: changeOrganization } = useChangeOrganization();

  const searchParams = new URLSearchParams(location.search);
  const orgIdInParams = searchParams.get("orgId");

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.data?.lang && event.data.lang !== lang) {
        dispatch(setUserLanguage(event.data.lang));
      }
    };
    languageChannel.addEventListener("message", listener);
    return () => languageChannel.removeEventListener("message", listener);
  }, [lang]);

  const handleChangeOrganization = async (orgId: string): Promise<void> => {
    try {
      await Promise.resolve(
        typeof changeOrganization === "function"
          ? changeOrganization(orgId)
          : undefined
      );
    } catch (err) {
      console.error(err);
    }
  };

  const removeOrgIdFromUrl = () => {
    if (!location.search.includes("orgId")) return;
    const params = new URLSearchParams(location.search);
    params.delete("orgId");
    navigate(`${location.pathname}${params.toString() ? `?${params}` : ""}`, {
      replace: true
    });
  };

  const fetchAndSetOrgSession = async () => {
    try {
      const { data } = await fetchLastAccessOrganization();
      localStorage.setItem(LOCAL_STORAGE_KEY.ORG_ID, data?.orgId as string);
      sessionStorage.setItem(SESSION_STORAGE_KEY.ORG_ID, data?.orgId as string);
      dispatch(setActiveOrganization(data?.orgId as string));
      removeOrgIdFromUrl();
    } catch (err) {
      console.error(err);
    }
  };

  // handle storing and updating current org
  useEffect(() => {
    if (!organizations) return;

    const orgIdInSessionStorage = sessionStorage.getItem(
      SESSION_STORAGE_KEY.ORG_ID
    );
    const orgIdInLocalStorage = localStorage.getItem(LOCAL_STORAGE_KEY.ORG_ID);

    const isValidOrg = (orgId: string) =>
      organizations.some((org) => org.orgId === orgId);

    if (orgIdInParams && isValidOrg(orgIdInParams)) {
      localStorage.setItem(LOCAL_STORAGE_KEY.ORG_ID, orgIdInParams);
      sessionStorage.setItem(SESSION_STORAGE_KEY.ORG_ID, orgIdInParams);
      dispatch(setActiveOrganization(orgIdInParams));
      handleChangeOrganization(orgIdInParams);
      navigate("/");
      return;
    }

    if (orgIdInSessionStorage && isValidOrg(orgIdInSessionStorage)) {
      sessionStorage.setItem(SESSION_STORAGE_KEY.ORG_ID, orgIdInSessionStorage);
      dispatch(setActiveOrganization(orgIdInSessionStorage));
      removeOrgIdFromUrl();
      return;
    }

    if (orgIdInLocalStorage && isValidOrg(orgIdInLocalStorage)) {
      sessionStorage.setItem(SESSION_STORAGE_KEY.ORG_ID, orgIdInLocalStorage);
      dispatch(setActiveOrganization(orgIdInLocalStorage));
      removeOrgIdFromUrl();
      return;
    }

    fetchAndSetOrgSession();
  }, [organizations]);

  // useEffect(() => {
  //   // handle storing and updating last active user email
  //   const lastActiveUser = localStorage.getItem("lastActiveEmail");

  //   if (!lastActiveUser || !email) return;
  //   if (email !== lastActiveUser) {
  //     const defaultOrganization = organizations?.[0];
  //     localStorage.setItem("lastActiveEmail", email || "");
  //     sessionStorage.setItem(
  //       SESSION_STORAGE_KEY.ORG_ID,
  //       defaultOrganization?.orgId || ""
  //     );
  //     dispatch(setActiveOrganization(defaultOrganization?.orgId || ""));
  //   }
  // }, [email]);

  // useEffect(() => {
  //   // trigger a reload when any other tab hits logout
  //   const handleStorage = (event: StorageEvent) => {
  //     if (event.key === "logout") {
  //       window.location.reload();
  //     }
  //   };

  //   window.addEventListener("storage", handleStorage);
  //   return () => window.removeEventListener("storage", handleStorage);
  // }, []);

  return (
    <IntlProvider
      locale={lang}
      defaultLocale={LANG.EN}
      messages={flattenMessages(messages[lang])}
    >
      <ThemeProvider>
        <GlobalStyles />

        <PageWrapperStyled>
          <Router />
          <LayerPortal />
        </PageWrapperStyled>

        <div style={{ display: "none" }}>
          Build version: {process.env.BUILD_VERSION_DATE}
        </div>
      </ThemeProvider>
    </IntlProvider>
  );
}
